import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './site-motion.css';

// Storefront only: content is visible by default, even without animation support.
export const SectionMotion = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!['/', '/courses', '/blog'].includes(pathname) && !pathname.startsWith('/course/')) return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (preference.matches || !window.IntersectionObserver || !Element.prototype.animate) return;
    const animations = new Set();
    const targets = [...document.querySelectorAll('.App section > div > *')].filter(element =>
      !element.closest('[data-scroll-scene], .fokeev-hero') && !['SCRIPT', 'STYLE'].includes(element.tagName) && element.getBoundingClientRect().height > 0);
    const reveal = element => {
      observer.unobserve(element);
      if (!element.classList.contains('motion-waiting')) return;
      element.classList.remove('motion-waiting');
      if (preference.matches) return;
      const base = getComputedStyle(element).transform;
      const resting = base === 'none' ? 'translateY(0)' : base;
      const animation = element.animate([
        { opacity: 0, transform: `translateY(12px) ${base === 'none' ? '' : base}` },
        { opacity: 1, transform: resting },
      ], { duration: 360, delay: Math.min([...element.parentElement.children].indexOf(element), 3) * 40,
        easing: 'cubic-bezier(0.23, 1, 0.32, 1)' });
      animations.add(animation);
      animation.finished.catch(() => {}).finally(() => animations.delete(animation));
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); });
    }, { rootMargin: '0px 0px 72px 0px', threshold: 0 });
    targets.forEach(element => {
      // No initial-screen fade, no hidden content when following an anchor.
      if (element.getBoundingClientRect().top < window.innerHeight + 72) return;
      element.classList.add('motion-waiting');
      observer.observe(element);
    });
    const finish = () => {
      observer.disconnect();
      targets.forEach(element => element.classList.remove('motion-waiting'));
      animations.forEach(animation => animation.cancel());
      animations.clear();
    };
    const keyboard = event => { if (event.key === 'Tab') finish(); };
    const focus = event => targets.filter(element => element.contains(event.target)).forEach(reveal);
    const changed = () => { if (preference.matches) finish(); };
    document.addEventListener('keydown', keyboard);
    document.addEventListener('focusin', focus);
    preference.addEventListener('change', changed);
    return () => {
      finish();
      document.removeEventListener('keydown', keyboard);
      document.removeEventListener('focusin', focus);
      preference.removeEventListener('change', changed);
    };
  }, [pathname]);
  return null;
};
